import React, { ReactElement, useState } from 'react';
import {
  Col, FileUpload, FormGroup, Heading, Label, LoaderButton, Row, Select,
} from 'idsk-react-components';
import SprievodnyDokumentRequest from '../../../model/pracovnyKrok/SprievodnyDokumentRequest';
import { SafeParameterSetter } from '../../../types/SafeParameterSetter';
import useFieldErrorMessages from '../../useFieldErrorMessages';
import PolozkaCiselnik from '../../../model/ciselniky/PolozkaCiselnik';
import { useClient } from '../../../context/ClientStatusContext';
import { createSprievodnyDokument } from '../../../client/pracovnyKrok/sprievodnyDokument/sprievodnyDokumentClient';
import { useSuccessMessageContext } from '../../../context/SuccessMessageContext';
import Uuid from '../../../model/Uuid';
import './sprievodnyDokumentAdd.scss';

export interface Props {
  typDokumentuList: PolozkaCiselnik[];
  legMaterialUuid: Uuid;
  stadiumUuid: Uuid;
  reloadSprievodnyDokumentList?: () => void;
}

const SprievodnyDokumentAdd = ({
  typDokumentuList,
  legMaterialUuid,
  stadiumUuid,
  reloadSprievodnyDokumentList,
}: Props): ReactElement => {
  const [sprievodnyDokumentRequest, setRequest] = useState<SprievodnyDokumentRequest>({
    legMaterialUuid,
    typDokumentu: undefined,
    sprievodnyDokument: undefined,
  });

  const [selectFileInputValue, setSelectFileInputValue] = useState('');

  const {
    getFieldErrorMessage,
    clearFieldErrorMessage,
  } = useFieldErrorMessages();
  const onUpdateField: SafeParameterSetter<SprievodnyDokumentRequest> = (fieldName, value) => {
    setRequest(prevState => ({
      ...prevState,
      [fieldName]: value,
    }));
    clearFieldErrorMessage(fieldName);
  };

  const {
    wrapClient,
    status,
  } = useClient(createSprievodnyDokument);
  const { displaySuccess } = useSuccessMessageContext();

  const handleSubmit = async (e): Promise<void> => {
    e.preventDefault();
    await wrapClient(sprievodnyDokumentRequest, stadiumUuid);
    if (reloadSprievodnyDokumentList) {
      reloadSprievodnyDokumentList();
    }
    displaySuccess('Sprievodný dokument bol úspešne pridaný.');
    onUpdateField('sprievodnyDokument', undefined);
    onUpdateField('typDokumentu', '');
    setSelectFileInputValue('');
  };

  return (
    <Row className="add-document__container">
      <Col>
        <Heading size="s">Pridať dokument</Heading>
        <Row>
          <Col widthDesktop="one-quarter">
            <FormGroup
              error={!!getFieldErrorMessage('typDokumentu')}
            >
              <Label htmlFor="typDokumentu">Typ dokumentu</Label>
              <Select
                id="typDokumentu"
                name="typDokumentu"
                value={sprievodnyDokumentRequest.typDokumentu}
                onChange={({ target }) => {
                  onUpdateField('typDokumentu', target.value);
                }}
                error={getFieldErrorMessage('typDokumentu')}
              >
                <option hidden value="" key="Vyberte">Vyberte</option>
                {(typDokumentuList && typDokumentuList?.length > 0) ? (
                  <>
                    {typDokumentuList.map(typ => (
                      <option value={typ.uuid} key={typ.uuid}>{typ.nazov}</option>))}
                  </>
                ) : (
                  <option key="emptyOption" value="emptyOption" disabled>
                    Nenašiel sa žiadny záznam.
                  </option>
                )}
              </Select>
            </FormGroup>
          </Col>
          <Col widthDesktop="three-quarters">
            <FormGroup
              error={!!getFieldErrorMessage('sprievodnyDokument')}
            >
              <Label htmlFor="dokument" className="sprievodny-dokument-add-file-upload-label">Dokument</Label>
              <FileUpload
                className="sprievodny-dokument-add-file-upload"
                id="dokument"
                error={getFieldErrorMessage('sprievodnyDokument')}
                value={selectFileInputValue}
                onFileUpload={(target) => {
                  onUpdateField('sprievodnyDokument', target.files[0]);
                  setSelectFileInputValue(target.value);
                }}
              />
            </FormGroup>
          </Col>
        </Row>
        <LoaderButton
          size="large"
          type="button"
          loading={status.loading}
          onClick={(e) => handleSubmit(e)}
          disableOutsideClickWhileLoading
        >Pridať
        </LoaderButton>
      </Col>
    </Row>
  );
};

export default SprievodnyDokumentAdd;
